import { useEffect, useState } from 'react'
import { History, X } from 'lucide-react'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { fmtDateTime } from '@/lib/format-date'
import { Abschnitt, FeldStatus, feldKlasse, mitStand, useFeld, useSpeicherstelle, useStellenDaten, type Speicherstelle } from '@/lib/speichern'

/**
 * AP-44 — **Hausregeln**: der Text des Betriebs je KI-Aufgabe, der bei jedem Aufruf hinter dem festen
 * Auftrag mitgeht. Geteilt zwischen den Einstellungen der Module (Eingangsrechnungen, Posteingang) —
 * die Endpunkte gehören dem Modul, deshalb bekommt die Komponente nur dessen Pfad.
 *
 * Gespeichert wird beim Verlassen des Felds (AP-36, Fall 1); jede Änderung ist am Server eine neue
 * Fassung. Antwortet der Server nicht mit einer Liste (Baustein nicht gebucht, Recht fehlt), gibt es
 * den Abschnitt nicht.
 */
export interface Hausregel {
  task: string
  label: string
  examples: string[]
  limit: number
  version: number
  text: string
  updatedAt: string | null
  updatedBy: string | null
}

interface Fassung {
  version: number
  text: string
  createdAt: string
  createdBy: string | null
}

const knopf = 'inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50'

export function Hausregeln({ pfad, hinweise = {} }: { pfad: string; hinweise?: Record<string, string> }) {
  const [zeilen, setZeilen] = useState<Hausregel[] | null>(null)
  useEffect(() => {
    let aus = false
    api
      .get<{ data: Hausregel[] }>(pfad)
      .then((r) => !aus && setZeilen(r.data.data))
      .catch(() => !aus && setZeilen(null))
    return () => {
      aus = true
    }
  }, [pfad])
  if (!zeilen?.length) return null

  return (
    <Abschnitt titel="Hausregeln für die KI">
      <div className="space-y-5 text-sm">
        <p className="text-muted-foreground">
          Hier bringst du der KI bei, wie die Unterlagen deines Betriebs zu lesen sind. Der Text wird bei jedem Aufruf der Aufgabe mitgeschickt und zählt zum KI-Verbrauch. Trag keine personenbezogenen Daten ein.
        </p>
        {zeilen.map((z) => (
          <HausregelFeld key={z.task} pfad={pfad} zeile={z} hinweis={hinweise[z.task]} />
        ))}
      </div>
    </Abschnitt>
  )
}

function HausregelFeld({ pfad, zeile, hinweis }: { pfad: string; zeile: Hausregel; hinweis?: string }) {
  const [verlauf, setVerlauf] = useState(false)
  const stelle = useSpeicherstelle<Hausregel>({
    schluessel: `ai-house-rule:${zeile.task}`,
    bezeichnung: `Hausregel „${zeile.label}"`,
    daten: zeile,
    schreibe: (patch, stand) => api.put<{ data: Hausregel }>(`${pfad}/${zeile.task}`, { text: (patch.text as string | undefined) ?? '' }, mitStand(stand)).then((r) => r.data.data),
    // Ohne Fassung gibt es keinen Stand — und eine Antwort ohne `updatedAt` ließe sonst den alten stehen.
    stand: (a) => a.updatedAt ?? '',
    keepalive: { url: `${pfad}/${zeile.task}`, method: 'PUT' },
  }) as Speicherstelle<unknown>
  const aktuell = useStellenDaten(stelle) as unknown as Hausregel
  const text = useFeld<string>(stelle, 'text', {
    label: 'Hausregel',
    // Leer heißt abgeschaltet und geht als leerer Text, nicht als `null`.
    lies: (t) => t,
    schreib: (w) => w ?? '',
    pruefe: (w) => ((w ?? '').trim().length > zeile.limit ? `Höchstens ${zeile.limit.toLocaleString('de-DE')} Zeichen.` : null),
  })
  const laenge = text.props.value.trim().length
  const id = `hausregel-${zeile.task}`

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label htmlFor={id} className="font-medium">
          {zeile.label}
        </label>
        {aktuell.version > 0 && (
          <button type="button" className={knopf} onClick={() => setVerlauf(true)}>
            <History className="h-3.5 w-3.5" aria-hidden /> Verlauf
          </button>
        )}
      </div>
      <textarea id={id} rows={6} className={`${feldKlasse} mt-1 resize-y font-mono text-[13px] leading-5`} placeholder={zeile.examples.join('\n')} {...text.props} />
      <div className="mt-1 flex flex-wrap items-start justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>
          {aktuell.version > 0
            ? `Fassung ${aktuell.version}${aktuell.updatedAt ? `, zuletzt geändert am ${fmtDateTime(aktuell.updatedAt)}` : ''}${aktuell.updatedBy ? ` von ${aktuell.updatedBy}` : ''}${aktuell.text ? '' : ' — abgeschaltet'}`
            : 'Noch keine Hausregel.'}
        </span>
        <span className={laenge > zeile.limit ? 'text-destructive' : undefined}>
          {laenge.toLocaleString('de-DE')} von {zeile.limit.toLocaleString('de-DE')} Zeichen
        </span>
      </div>
      <FeldStatus status={text} />
      {zeile.examples.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          Zum Beispiel: {zeile.examples.join(' · ')}
        </p>
      )}
      {hinweis && <p className="mt-1 text-xs text-muted-foreground">{hinweis}</p>}
      {verlauf && (
        <Verlauf
          pfad={pfad}
          zeile={aktuell}
          schliessen={() => setVerlauf(false)}
          wiederherstellen={(alt) => stelle.sende({ kennung: 'text', felder: ['text'], labels: { text: 'Hausregel' }, patch: { text: alt } })}
        />
      )}
    </div>
  )
}

/** Alle Fassungen einer Hausregel, jüngste zuerst. „Wiederherstellen" schreibt den alten Text als neue Fassung. */
function Verlauf({
  pfad,
  zeile,
  schliessen,
  wiederherstellen,
}: {
  pfad: string
  zeile: Hausregel
  schliessen: () => void
  wiederherstellen: (text: string) => Promise<{ art: string; text?: string }>
}) {
  const [fassungen, setFassungen] = useState<Fassung[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  useEffect(() => {
    let aus = false
    api
      .get<{ data: Fassung[] }>(`${pfad}/${zeile.task}/versions`, { params: { limit: 50 } })
      .then((r) => !aus && setFassungen(r.data.data))
      .catch((e) => !aus && setFehler(apiErrorText(e, 'Der Verlauf ließ sich nicht laden.')))
    return () => {
      aus = true
    }
  }, [pfad, zeile.task])
  useEffect(() => {
    const taste = (e: KeyboardEvent) => e.key === 'Escape' && schliessen()
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [schliessen])

  const hole = async (alt: string) => {
    setLaeuft(true)
    setFehler(null)
    const ergebnis = await wiederherstellen(alt)
    setLaeuft(false)
    if (ergebnis.art === 'fehler') setFehler(ergebnis.text ?? 'Das Wiederherstellen hat nicht geklappt.')
    else schliessen()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && schliessen()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Verlauf der Hausregel „${zeile.label}"`}
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg border border-border p-4 text-sm shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="font-semibold">Verlauf der Hausregel</p>
            <p className="text-xs text-muted-foreground">{zeile.label}</p>
          </div>
          <button type="button" className="rounded p-1 hover:bg-muted" aria-label="Schließen" onClick={schliessen}>
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        {fehler && <p className="mb-2 text-destructive">{fehler}</p>}
        {!fassungen && !fehler && <p className="text-muted-foreground">Lädt …</p>}
        <div className="space-y-3 overflow-auto">
          {fassungen?.map((f) => {
            const gilt = f.version === zeile.version
            return (
              <div key={f.version} className="rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <span className="font-medium">Fassung {f.version}</span>
                    <span className="text-muted-foreground">
                      {' '}
                      · {fmtDateTime(f.createdAt)}
                      {f.createdBy ? ` · ${f.createdBy}` : ''}
                    </span>
                    {gilt && <span className="ml-2 rounded bg-primary/15 px-1.5 py-0.5 text-xs text-primary">gilt</span>}
                  </span>
                  {!gilt && (
                    <button type="button" className={knopf} disabled={laeuft} onClick={() => void hole(f.text)}>
                      Wiederherstellen
                    </button>
                  )}
                </div>
                {f.text ? <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[13px] leading-5">{f.text}</pre> : <p className="mt-2 text-muted-foreground">abgeschaltet (leer)</p>}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
